import React, { useState } from 'react';
import '../styles/products.css';

const EMPTY_PRODUCT = {
  product_name: '',
  sku: '',
  brand: '',
  category: '',
  unit: 'pcs',
  hsn_code: '',
  gst_rate: '0',
  low_stock_alert: '0',
};

const ProductForm = ({
  initialValues,
  onSubmit,
  submitLabel = 'Save Product',
  loading = false,
}) => {
  const [form, setForm] = useState(() => ({
    ...EMPTY_PRODUCT,
    ...initialValues,
    brand: initialValues?.brand ?? '',
    category: initialValues?.category ?? '',
    hsn_code: initialValues?.hsn_code ?? '',
    gst_rate: String(initialValues?.gst_rate ?? 0),
    low_stock_alert: String(initialValues?.low_stock_alert ?? 0),
  }));

  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: '',
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.product_name.trim()) {
      nextErrors.product_name = 'Product name is required.';
    } else if (form.product_name.trim().length > 200) {
      nextErrors.product_name = 'Maximum 200 characters.';
    }

    if (!form.sku.trim()) {
      nextErrors.sku = 'SKU is required.';
    } else if (form.sku.trim().length > 100) {
      nextErrors.sku = 'Maximum 100 characters.';
    }

    if (!form.unit.trim()) {
      nextErrors.unit = 'Unit is required.';
    }

    const gst = Number(form.gst_rate);
    if (
      form.gst_rate.trim() === '' ||
      !Number.isFinite(gst) ||
      gst < 0 ||
      gst > 100
    ) {
      nextErrors.gst_rate = 'GST must be between 0 and 100.';
    }

    const lowStock = Number(form.low_stock_alert);
    if (
      form.low_stock_alert.trim() === '' ||
      !Number.isFinite(lowStock) ||
      lowStock < 0
    ) {
      nextErrors.low_stock_alert =
        'Low-stock alert must be zero or greater.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    const payload = {
      product_name: form.product_name.trim(),
      sku: form.sku.trim(),
      brand: form.brand.trim() || null,
      category: form.category.trim() || null,
      unit: form.unit.trim(),
      hsn_code: form.hsn_code.trim() || null,
      gst_rate: Number(form.gst_rate),
      low_stock_alert: Number(form.low_stock_alert),
    };

    await onSubmit(payload);
  };

  const field = (
    name,
    label,
    options = {}
  ) => (
    <div className="product-form-field">
      <label htmlFor={name}>{label}</label>
      <input
        id={name}
        name={name}
        type={options.type || 'text'}
        value={form[name]}
        onChange={handleChange}
        placeholder={options.placeholder || ''}
        min={options.min}
        max={options.max}
        step={options.step}
        maxLength={options.maxLength}
        required={options.required}
        disabled={loading}
      />
      {errors[name] && (
        <span className="product-field-error">
          {errors[name]}
        </span>
      )}
    </div>
  );

  return (
    <form className="product-form" onSubmit={handleSubmit}>
      <div className="product-form-grid">
        {field('product_name', 'Product Name', {
          placeholder: 'e.g. Paracetamol 500mg',
          maxLength: 200,
          required: true,
        })}

        {field('sku', 'SKU / Product Code', {
          placeholder: 'e.g. MED-001',
          maxLength: 100,
          required: true,
        })}

        {field('brand', 'Brand', {
          placeholder: 'Enter brand',
          maxLength: 100,
        })}

        {field('category', 'Category', {
          placeholder: 'e.g. Medicine',
          maxLength: 100,
        })}

        {field('unit', 'Unit', {
          placeholder: 'pcs, box, bottle, kg',
          maxLength: 30,
          required: true,
        })}

        {field('hsn_code', 'HSN Code', {
          placeholder: 'Enter HSN code',
          maxLength: 20,
        })}

        {field('gst_rate', 'GST Rate (%)', {
          type: 'number',
          min: 0,
          max: 100,
          step: '0.01',
          required: true,
        })}

        {field('low_stock_alert', 'Low Stock Alert', {
          type: 'number',
          min: 0,
          step: '0.001',
          required: true,
        })}
      </div>

      <div className="product-form-actions">
        <button
          type="submit"
          className="product-primary-button"
          disabled={loading}
        >
          {loading ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default ProductForm;